import React, { Fragment, useRef, useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import TableFooter from '@mui/material/TableFooter';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import Box from '@mui/material/Box';
import styled from 'styled-components';
import * as XLSX from 'xlsx'
import Popup from 'reactjs-popup';
import PopupTable from '../PopupTable';
import { HeadingWrap, Span, CommonButton } from '../../Styles/CommonStyles';
import { useUpdateBulkUsersMutation, useGetUsersQuery } from '../../features/users/usersApiSlice';
import CircularProgress from '@mui/material/CircularProgress';
import ExcelDownload from '../../data/exceldownload';

const EMAIL_REGEX = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

const Displaydiv1 = styled.div`
`
const SubmitButton = styled(CommonButton)`
  height:"40px";
  margin-top:"0px";
  margin-bottom:"0px";
`
const CustomFileUploadButton = styled.label`
  background-color: #4caf50; /* Green */
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
`
const BulkWrapper = styled.div`
  display: flex;
  align-items:center;
`
const InputFile = styled.input`
  width: 180px;
  display: none;
`
const SelectFileReset = styled.button`
  margin-right: 10px;
`
const ResultContainer = styled.div`
  margin-top: 20px;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #f9f9f9;
`
const SuccessMessage = styled.div`
  color: green;
  margin-bottom: 10px;
`
const ErrorMessage = styled.div`
  color: red;
  margin-bottom: 10px;
`
const UserList = styled.ul`
  list-style-type: none;
  padding-left: 10px;
  margin-top: 5px;
`
const UserItem = styled.li`
  margin-bottom: 3px;
`

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

// Function to check for duplicates
const checkDuplicates = (arr, key) => {
  const seen = new Set();
  const duplicates = new Set();

  arr.forEach(item => {
    const value = item[key];
    if (seen.has(value)) {
      duplicates.add(value);
    } else {
      seen.add(value);
    }
  });

  return duplicates;
};

const UpdateBulkUsers = () => {
  const { users } = useGetUsersQuery("users", {
    selectFromResult: ({ data }) => ({
      users: data?.entities
    }),
  });

  const [updateBulkUsers, {
    isLoading,
    isSuccess,
    isError,
    error,
    data: responseData
  }] = useUpdateBulkUsersMutation();

  const inputFile = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [nonExistingEmails, setNonExistingEmails] = useState([]);
  const [updateResult, setUpdateResult] = useState(null);

  const handleButtonClick = () => {
    inputFile.current.click();
  };

  const validateFile = (data) => {
    // Check for invalid email formats
    const invalid = data.filter(item => !EMAIL_REGEX.test(item.email));
    setInvalidEmails(invalid);

    // Check for non-existing users
    if (users) {
      const existingEmails = Object.values(users).map(user => user.email);
      const nonExisting = data.filter(item => 
        EMAIL_REGEX.test(item.email) && !existingEmails.includes(item.email)
      );
      setNonExistingEmails(nonExisting);
    }

    return data.filter(item => 
      EMAIL_REGEX.test(item.email) && 
      users && 
      Object.values(users).some(user => user.email === item.email)
    );
  };

  const onBulkSubmit = async (e) => {
    e.preventDefault();
    
    if (!file || file.length === 0) {
      setFileError("No valid users to update");
      return;
    }

    const validUsers = validateFile(file);
    
    if (validUsers.length === 0) {
      setFileError("No valid users found for update");
      return;
    }

    try {
      // Ensure numeric values for mobile and landline are converted to strings
      const processedUsers = validUsers.map(user => {
        const processed = { ...user };
        
        // Convert mobileNo to string if it's a number
        if (typeof processed.mobileNo === 'number') {
          processed.mobileNo = processed.mobileNo.toString();
        }
        
        // Convert officeLandLine to string if it's a number
        if (typeof processed.officeLandLine === 'number') {
          processed.officeLandLine = processed.officeLandLine.toString();
        }
        
        return processed;
      });
      console.log("Processed Users:", processedUsers);
      
      const result = await updateBulkUsers({ "data": processedUsers });
      console.log(result);
      setUpdateResult(result.data?.results);
      
      // Show alert message after operation completes
      if (result.data?.results) {
        const { successCount, failedCount } = result.data.results;
        if (failedCount > 0) {
          alert(`Bulk update completed!\n\n✅ Successfully updated: ${successCount} users\n❌ Failed to update: ${failedCount} users\n\nPlease check the results below for details.`);
        } else {
          alert(`✅ Bulk update completed successfully!\n\nAll ${successCount} users have been updated.`);
        }
      }
    } catch (err) {
      console.error("Error updating users:", err);
      alert("❌ Error occurred during bulk update. Please try again.");
    }
  };

  const deleteSelectFile = () => {
    if (inputFile.current) {
      inputFile.current.value = "";
      inputFile.current.type = "text";
      inputFile.current.type = "file";
      setFile(null);
      setFileName("");
      setInvalidEmails([]);
      setNonExistingEmails([]);
      setUpdateResult(null);
    }
  };

  const fileType = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
  
  const handleChangeFile = (e) => {
    let selectedFile = e.target.files[0];
    if (selectedFile) {
      if (fileType.includes(selectedFile.type)) {
        setFileName(selectedFile.name);
        let reader = new FileReader();
        reader.readAsArrayBuffer(selectedFile);
        reader.onload = (e) => {
          setFileError(null);
          const workbook = XLSX.read(e.target.result, { type: 'buffer' });
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];
          const data = XLSX.utils.sheet_to_json(worksheet);
          
          // Check for duplicate emails in the upload
          const duplicateEmails = checkDuplicates(data, "email");
          if ([...duplicateEmails]?.length) {
            alert(`Please check duplicate entries in uploaded excel: ${[...duplicateEmails]}`);
          }
          
          setFile(data);
          validateFile(data);
        };
      } else {
        setFileError("Please select correct file type (.xlsx or .xls)");
        setFile(null);
      }
    } else {
      setFileError("No file selected");
      setFile(null);
    }
  };

  // Template data for Excel download
  const templateData = [
    { email: "john@example.com", firstName: "John", middleName: "Robert", lastName: "Doe", mobileNo: "1234567890", officeLandLine: "1234567890", employeeID: "EMP001", officeAddress: "New York Office", active: true },
    { email: "jane@example.com", firstName: "Jane", middleName: "", lastName: "Smith", password: "NewPassword123", active: true },
    { email: "bob@example.com", mobileNo: "9876543210", active: false },
  ];

  return (
    <>
      <Displaydiv1>
        <form onSubmit={onBulkSubmit}>
          <HeadingWrap>
            <Span>Update Bulk Users</Span>
          </HeadingWrap>

          <BulkWrapper>
            <ExcelDownload data={templateData} filename="update_users_template" sheetName="Template" button={true} />
            <InputFile type="file" onChange={handleChangeFile} ref={inputFile} />
            <CustomFileUploadButton htmlFor="file-upload" onClick={handleButtonClick}>
              Upload File
            </CustomFileUploadButton>
            
            {file && (
              <>
                <span>{fileName}</span>
                <SelectFileReset type='button' onClick={() => deleteSelectFile()}>×</SelectFileReset>
                <Popup trigger={<SubmitButton type='button'>View File</SubmitButton>} position="right center" modal nested>
                  <PopupTable props={file} />
                </Popup>
              </>
            )}
            
            <SubmitButton type='submit' disabled={isLoading || !file}>Submit</SubmitButton>
          </BulkWrapper>
        </form>
        
        {fileError && <ErrorMessage>{fileError}</ErrorMessage>}
        
        {invalidEmails.length > 0 && (
          <ResultContainer>
            <ErrorMessage>Invalid email formats:</ErrorMessage>
            <UserList>
              {invalidEmails.map((item, index) => (
                <UserItem key={`invalid-${index}`}>{item.email || "Missing email"}</UserItem>
              ))}
            </UserList>
          </ResultContainer>
        )}
        
        {nonExistingEmails.length > 0 && (
          <ResultContainer>
            <ErrorMessage>Emails not found in the system:</ErrorMessage>
            <UserList>
              {nonExistingEmails.map((item, index) => (
                <UserItem key={`nonexisting-${index}`}>{item.email}</UserItem>
              ))}
            </UserList>
          </ResultContainer>
        )}
        
        {isLoading && <CircularProgress />}
        
        {updateResult && (
          <ResultContainer>
            <SuccessMessage>Update completed: {updateResult.successCount} users updated successfully</SuccessMessage>
            
            {updateResult.successfulUpdates.length > 0 && (
              <>
                <div>Successfully updated:</div>
                <UserList>
                  {updateResult.successfulUpdates.map((email, index) => (
                    <UserItem key={`success-${index}`}>{email}</UserItem>
                  ))}
                </UserList>
              </>
            )}
            
            {updateResult.failedUpdates.length > 0 && (
              <>
                <ErrorMessage>Failed to update {updateResult.failedCount} users:</ErrorMessage>
                <UserList>
                  {updateResult.failedUpdates.map((item, index) => (
                    <UserItem key={`failed-${index}`}>
                      {item.email}: {item.reason}
                    </UserItem>
                  ))}
                </UserList>
              </>
            )}
          </ResultContainer>
        )}
      </Displaydiv1>
    </>
  );
};

export default UpdateBulkUsers;